import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const REPORT_DEFS = {
  detail: {
    title: 'Inventory Detail',
    icon: '📋',
    description: 'Select one to many counts and review item-level totals.',
  },
  value: {
    title: 'Inventory Value',
    icon: '💰',
    description: 'Category totals and full inventory valuation by count set.',
  },
  order: {
    title: 'Order Report',
    icon: '🛒',
    description: 'Order value grouped by vendor with contact details.',
  },
  'purchase-orders': {
    title: 'Purchase Orders',
    icon: '📨',
    description: 'Search, filter, and resend vendor purchase orders.',
  },
  cogs: {
    title: 'End of Period',
    icon: '📊',
    description: 'End of period COGS report per P&L category with trend graph.',
    // Data-entry wizard — a stray click on the backdrop must not discard
    // in-progress work, so this modal only closes via its Close buttons.
    staticBackdrop: true,
  },
  'item-price-change': {
    title: 'Price Change Report',
    icon: '📈',
    description: 'Review actual item price movements by item, vendor, and category.',
  },
  'theoretical-food-cost': {
    title: 'Theoretical Food Cost',
    icon: '🍽️',
    description: 'What the items you sold should have cost, based on POS product mix and completed plate costs.',
  },
  item: {
    title: 'Inventory Item Database',
    icon: '🗂️',
    description: 'Full store item data — search, sort, and download all or a subset.',
  },
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const NESTED_OVERLAY_SELECTORS = [
  '[role="dialog"]:not(.report-modal-overlay)',
  '.modal.show',
  '.vos-overlay',
  '.vos-preview-overlay',
  '.mco-modal-overlay',
  '.import-modal-overlay',
  '.import-tool-modal-overlay',
].join(', ');

const hasNestedOverlay = () => document.querySelector(NESTED_OVERLAY_SELECTORS) !== null;

const ReportModal = ({ reportTypeId, onClose, footerActions, children }) => {
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const prevFocusRef = useRef(null);
  const reportDef = REPORT_DEFS[reportTypeId] || {};

  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dialog = dialogRef.current;

    const getFocusable = () => (dialog ? [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)] : []);

    const focusFirst = () => {
      const els = getFocusable();
      if (els.length) els[0].focus();
    };

    requestAnimationFrame(focusFirst);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!hasNestedOverlay()) onClose();
        return;
      }
      if (e.key === 'Tab') {
        const els = getFocusable();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      try { if (prevFocusRef.current) prevFocusRef.current.focus(); } catch (_) {}
    };
  }, [onClose]);

  const handleBackdrop = (e) => {
    if (reportDef.staticBackdrop) return;
    if (e.target === overlayRef.current) onClose();
  };

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="report-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={reportDef.title || 'Report'}
      onClick={handleBackdrop}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 8500,
        background: 'rgba(30, 18, 10, 0.52)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      <div
        ref={dialogRef}
        className="report-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 8px 48px rgba(30, 18, 10, 0.22)',
          width: '100%',
          maxWidth: 1100,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 3rem)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem 1.25rem',
          borderBottom: '1.5px solid #e8ddd0',
          flexShrink: 0,
          background: '#faf8f5',
        }}>
          {reportDef.icon && (
            <span style={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>
              {reportDef.icon}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{
              margin: 0,
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: '1rem',
              color: '#3D2518',
              lineHeight: 1.25,
            }}>
              {reportDef.title || 'Report'}
            </h4>
            {reportDef.description && (
              <p style={{
                margin: '0.2rem 0 0',
                fontSize: '0.8rem',
                color: '#7a6e66',
                lineHeight: 1.4,
              }}>
                {reportDef.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              lineHeight: 1,
              color: '#8b7b73',
              cursor: 'pointer',
              padding: '0.2rem 0.4rem',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 6,
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#ede8e3'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '1.25rem',
          WebkitOverflowScrolling: 'touch',
        }}>
          {children}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '0.75rem 1.25rem',
          borderTop: '1.5px solid #e8ddd0',
          flexShrink: 0,
          background: '#faf8f5',
          gap: '0.5rem',
        }}>
          {footerActions && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              {footerActions}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#fff',
              border: '1.5px solid #d4c4b0',
              color: '#5A4A42',
              padding: '0.45rem 1.35rem',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.88rem',
              cursor: 'pointer',
              fontFamily: "'Montserrat', sans-serif",
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f0eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @media print {
          .report-modal-overlay { display: none !important; }
        }
        @media (max-width: 768px) {
          .report-modal-overlay {
            padding: 0 !important;
            align-items: flex-start !important;
          }
          .report-modal-dialog {
            border-radius: 0 !important;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            height: 100vh;
            height: 100dvh;
            max-width: 100% !important;
          }
        }
      `}</style>
    </div>,
    document.body,
  );
};

export default ReportModal;
